import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import { useFormContext } from "react-hook-form"

import { color, mq } from "../../utils/style-components"

type ContactFormProps = {
    isSendError: boolean
}

const ContactForm:React.FC<ContactFormProps> = ({isSendError}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  return (
    <>
      {isSendError && (
      <SendErrorMessage />
      )}
      <InputRow label="お名前" required>
        <input
          placeholder="氏名を入力"
          {...register(`name`, { required: true })}
          css={inputStyle}
        />
        {errors.name && <ErrorMessage>お名前を入力してください。</ErrorMessage>}
      </InputRow>
      <InputRow label="メールアドレス" required>
        <input
          type="email"
          placeholder="メールアドレスを入力"
          {...register(`email`, {
            required: true,
            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
          })}
          css={inputStyle}
        />
        {errors.email?.type === "required" && (
          <ErrorMessage>メールアドレスを入力してください。</ErrorMessage>
        )}
        {errors.email?.type === "pattern" && (
          <ErrorMessage>メールアドレスの形式が正しくありません。</ErrorMessage>
        )}
      </InputRow>

      <InputRow label="社名・所属">
        <input
          placeholder="会社名・所属を入力"
          {...register(`company`)}
          css={inputStyle}
        />
      </InputRow>
      <InputRow label="電話番号">
        <input
          placeholder="電話番号を入力"
          {...register(`tel`)}
          css={inputStyle}
        />
      </InputRow>
      <InputRow label="URL">
        <input
          placeholder="御社ウェブサイトのURLを入力"
          {...register(`url`)}
          css={inputStyle}
        />
      </InputRow>
      <InputRow label="お問合せ内容">
        <textarea {...register(`message`)} rows={5} css={inputStyle} />
      </InputRow>
      <div
        css={css`
          margin-top: min(80px, 10vw);
          padding: min(40px, 10vw) 0;
          border-top: solid 1px ${color.font};
          border-bottom: solid 1px ${color.font};
        `}
      >
        <h3
          css={css`
            ${mq(
              `pc`,
              css`
                font-size: 1.8rem;
              `
            )}
            ${mq(
              `sp`,
              css`
                font-size: 1.6rem;
              `
            )}
          `}
        >
          個人情報の取り扱いについて
        </h3>
        <p
          css={css`
            padding: min(40px, 10vw) 0;
            ${mq(
              `pc`,
              css`
                font-size: 1.5rem;
              `
            )}
            ${mq(
              `sp`,
              css`
                font-size: 1.3rem;
              `
            )}
          `}
        >
          ご登録いただきました情報は、株式会社FIELD MANAGEMENT EXPANDにおいて今後のマーケティング活動、営業活動の参考とさせていただく他、電子メール等によるイベント開催情報などの各種情報提供に利用させていただきます。
          <br />
          また、弊社において厳正な選定をいたしました委託業者が、利用目的の範囲内で使用することがございます。
          <br />
          尚、ご登録いただきました個人情報をご本人の承諾なしに目的外利用すること、及び第三者に提供することはありません。
          <br />
          <br />
          個人情報に関するお取り扱いにつきましては、当社親会社のKANAMEL株式会社が定める「個人情報保護方針」「KANAMELグループ個人番号及び特定個人情報の適正な取扱いに関する基本方針」に準じます。
          <br />
          <a
            href="https://group-fm.com/policy/"
            target="_blank"
            rel="noopener noreferrer"
            css={css`
              color: ${color.blue};
            `}
          >
            https://group-fm.com/policy/
          </a>
          をご参照ください。
          <br />
          <br />
          同意いただける場合、下記チェックボックスにチェックをお願いいたします。
        </p>
        <label
          css={css`
            cursor: pointer;
            display: inline-block;
            margin-top: min(40px, 10vw);
            input[type="checkbox"] {
              display: none;
              &:checked + div::after {
                opacity: 1;
                width: 15px;
                height: 10px;
              }
            }
          `}
        >
          <input
            type="checkbox"
            {...register(`agreement`, {
              validate: value => value === true,
            })}
          />
          <div
            css={css`
              position: relative;
              display: flex;
              align-items: center;
              width: 260px;
              font-weight: bold;
              &::before {
                display: inline-block;
                content: "";
                width: 16px;
                height: 16px;
                border: solid 1px ${color.font};
                margin: 2px 0.8em 0 0;
              }
              &::after {
                display: block;
                content: "";
                position: absolute;
                top: 45%;
                left: 0;
                width: 0;
                height: 0;
                border-left: 3px solid ${color.blue};
                border-bottom: 3px solid ${color.blue};
                transform-origin: 0 0;
                transform: rotate(-45deg);
                opacity: 0;
                transition: height 0.2s, width 0.1s 0.1s;
              }
            `}
          >
            <span>個人情報の取り扱いに同意する</span>
          </div>
        </label>
        {errors.agreement && (
          <ErrorMessage>個人情報の取り扱いの同意が必要です。</ErrorMessage>
        )}
      </div>
      <div
        css={css`
          display: flex;
          justify-content: center;
          padding: min(87px, 12vw) 0 0;
        `}
      >
        <button
          type="submit"
          css={css(
            buttonStyle,
            css`
              background: ${color.blue};
            `
          )}
        >
          確認する
        </button>
      </div>
    </>
  )
}

const InputWrap = styled.div`
  margin-top: min(40px, 5vw);
  &:first-of-type {
    margin-top: 0;
  }
`

type InputRowProps = {
  label: string
  required?: boolean
}
const InputRow: React.FC<InputRowProps> = ({ label, required, children }) => (
  <InputWrap>
    <label>
      <span
        css={css`
          font-weight: bold;
          ${required &&
          `
            &::after {
              content: '※';
              color: ${color.red};
              margin-left: 0.3em;
            }
          `}
        `}
      >
        {label}
      </span>
      <div>{children}</div>
    </label>
  </InputWrap>
)
const ErrorMessage = styled.div`
  color: ${color.red};
`
const inputStyle = css`
  border: 1px solid ${color.font};
  width: 100%;
  max-width: 574px;
  padding: 1px 0.5em;
`
const buttonStyle = css`
  font-weight: bold;
  font-size: 1.6rem;
  line-height: 1;
  color: ${color.white};
  width: 270px;
  height: 70px;
`

const SendErrorMessage: React.FC = () => (
  <div
    css={css`
      color: ${color.red};
    `}
  >
    <h4>メッセージの送信に失敗しました</h4>
    <p>
      何度も送信に失敗するようでしたら、お手数ですが時間を置いて再度お問い合わせをお願い致します。
    </p>
  </div>
)

export default ContactForm
